import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("¡Bienvenido de vuelta!");
      navigate(user.role === "superadmin" ? "/admin" : "/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12">
        <div className="max-w-md mx-auto w-full">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="CIA Factura℗" className="h-10 w-auto" />
            <span className="font-bold text-xl text-slate-900">CIA Factura℗</span>
          </Link>

          <h1 className="text-3xl font-bold text-slate-900 mb-2">Iniciar Sesión</h1>
          <p className="text-slate-600 mb-8">Ingresa a tu cuenta para continuar</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                className="cia-input py-6"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                <Link to="/forgot-password" className="text-sm text-[#5B9BD5] hover:underline">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="cia-input py-6 pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full cia-btn-primary py-6 text-lg"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-600">
            ¿No tienes una cuenta?{" "}
            <Link to="/register" className="text-[#5B9BD5] font-medium hover:underline" data-testid="register-link">
              Regístrate gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image/Branding */}
      <div className="hidden lg:flex lg:w-1/2 cia-gradient items-center justify-center p-12">
        <div className="max-w-md text-center text-white">
          <img src={LOGO_URL} alt="CIA Factura℗" className="h-24 w-auto mx-auto mb-8 brightness-200" />
          <h2 className="text-3xl font-bold mb-4">Control Integral Administrativo</h2>
          <p className="text-white/80 text-lg">
            La plataforma de facturación electrónica más completa de México
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
